import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useRecoilValue } from "recoil";
import { zoomSettingsState } from "../../../../../recoil/zoomSettings";
import { userState } from "../../../../../recoil/auth";
import { isSpecialUser } from "../../../../../utils/environment";
import "../AdDetailModal.css";
import {
  getNaverShoppingRankAPI,
  updateKeywordAPI,
  patchAdExtensionAPI,
  deleteKeywordAPI,
} from "../../../../../api";

const ShoppingKeywordEditModal = ({
  isOpen,
  onClose,
  adData,
  keywordData,
  onSave,
}) => {
  const [keywordRanks, setKeywordRanks] = useState([]);
  const [tempKeywords, setTempKeywords] = useState([]); // 임시 키워드 상태 추가
  const [keywordLoadingIndex, setKeywordLoadingIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasInitialized = useRef(false);
  // zoomSettings에서 주문 마감 시간 가져오기
  const zoomSettings = useRecoilValue(zoomSettingsState);
  const user = useRecoilValue(userState);
  const userIsSpecial = isSpecialUser(user?.id);

  // 모달이 열릴 때 키워드 데이터 초기화
  useEffect(() => {
    if (isOpen && keywordData && adData && !hasInitialized.current) {
      hasInitialized.current = true;
      setIsLoading(true);

      // 기존 키워드들을 keywordRanks에 추가 (MAIN 타입 제외)
      const existingKeywords = keywordData
        .filter((keyword) => keyword.type !== "MAIN") // MAIN 타입 제외
        .map((keyword, index) => ({
          keyword: keyword.name || "",
          rank: keyword.currentRank || 999,
          selected: true,
          type: keyword.type || "SUB",
          isExisting: true, // 기존 키워드 표시
          keyword_id: keyword.keyword_id, // 올바른 필드명 사용
        }));

      // 새로운 키워드 생성 (기존 키워드 제외)
      const newKeywords = generateNewKeywords(existingKeywords);

      // 기존 키워드와 새 키워드를 합치고 순위 조회
      const allKeywords = [...existingKeywords, ...newKeywords];
      setKeywordRanks(allKeywords);

      // 모든 키워드(기존 + 새로 생성된)의 순위 조회 (최초 한 번만)
      fetchKeywordRanksForAllKeywords(allKeywords);
    }
  }, [isOpen, keywordData, adData]);

  // 모달이 닫힐 때 초기화 플래그 리셋
  useEffect(() => {
    if (!isOpen) {
      hasInitialized.current = false;
    }
  }, [isOpen]);

  // 새로운 키워드 생성 함수
  const generateNewKeywords = (existingKeywords) => {
    // 기존 키워드 이름들을 소문자로 변환하여 중복 체크용 배열 생성
    const existingKeywordNames = existingKeywords.map((k) =>
      k.keyword.toLowerCase().trim()
    );

    // keywordData에서도 SUB 타입 키워드들의 이름을 추가로 체크
    const keywordDataNames = (keywordData || [])
      .filter((k) => k.type === "SUB")
      .map((k) => (k.name || "").toLowerCase().trim())
      .filter((name) => name !== "");

    // 모든 기존 키워드 이름들을 합쳐서 중복 체크
    const allExistingNames = [...existingKeywordNames, ...keywordDataNames];

    const missionName = adData.missionName || "";

    // 상품명에서 키워드 추출
    const words = missionName
      .split(/[,\s]+/)
      .filter((word) => word.trim().length > 0 && word.length > 1)
      .filter((word) => !allExistingNames.includes(word.toLowerCase().trim()));

    // 단어 조합 생성
    let keywordCombinations = [];

    // 1개씩 조합
    words.forEach((word) => {
      const trimmedWord = word.trim();
      if (
        trimmedWord &&
        !allExistingNames.includes(trimmedWord.toLowerCase())
      ) {
        keywordCombinations.push(trimmedWord);
      }
    });

    // 2개씩 조합
    for (let i = 0; i < words.length - 1; i++) {
      for (let j = i + 1; j < words.length; j++) {
        const combination = `${words[i].trim()} ${words[j].trim()}`;
        if (!allExistingNames.includes(combination.toLowerCase())) {
          keywordCombinations.push(combination);
        }
      }
    }

    // 3개씩 조합
    for (let i = 0; i < words.length - 2; i++) {
      for (let j = i + 1; j < words.length - 1; j++) {
        for (let k = j + 1; k < words.length; k++) {
          const combination = `${words[i].trim()} ${words[j].trim()} ${words[
            k
          ].trim()}`;
          if (!allExistingNames.includes(combination.toLowerCase())) {
            keywordCombinations.push(combination);
          }
        }
      }
    }

    // 중복 제거 (새로 생성된 키워드들 간의 중복)
    const uniqueCombinations = [...new Set(keywordCombinations)];

    // 최대 30개로 제한하고 새로운 키워드 객체 생성
    const limitedCombinations = uniqueCombinations.slice(0, 30);

    return limitedCombinations.map((keyword) => ({
      keyword: keyword,
      rank: 999,
      selected: false,
      type: "SUB",
      isExisting: false, // 새로운 키워드 표시
    }));
  };

  // 키워드 추가 함수 (임시 키워드에 추가)
  const addKeywordRank = () => {
    setTempKeywords((prev) => [
      ...prev,
      {
        keyword: "",
        rank: 0, // 새로운 키워드는 순위 0으로 시작
        selected: true,
        type: "SUB",
        isExisting: false,
        tempId: `temp_${Date.now()}_${Math.random()}`, // 문자열로 고유 ID 생성
      },
    ]);
  };

  // 키워드 토글 함수 (선택/해제)
  const handleToggle = (index) => {
    const updatedRanks = [...keywordRanks];
    const currentSelected = updatedRanks[index].selected;
    const currentRank = updatedRanks[index].rank;
    const allowRanking = zoomSettings?.allowRanking || 30; // zoomSettings에서 가져오기
    // 특정 유저는 순위 제한 예외
    const effectiveAllowRanking = userIsSpecial ? Infinity : allowRanking;

    if (currentSelected) {
      // 선택 해제는 항상 가능 (순위와 관계없이)
      updatedRanks[index].selected = false;
    } else {
      // 순위가 allowRanking보다 높으면 선택 불가 (특정 유저는 예외)
      if (currentRank > effectiveAllowRanking) {
        alert(`${allowRanking}위 초과 키워드는 선택할 수 없습니다.`);
        return;
      }

      // 선택하려면 현재 선택된 개수가 10개 미만이어야 함
      const selectedCount = updatedRanks.filter((item) => item.selected).length;
      if (selectedCount < 10) {
        updatedRanks[index].selected = true;
      } else {
        alert("최대 10개까지만 선택할 수 있습니다.");
        return;
      }
    }

    setKeywordRanks(updatedRanks);
  };

  // 키워드 제거 함수
  const removeKeywordRank = (index) => {
    setKeywordRanks((prev) => {
      const item = prev[index];

      // isExisting이 true인 키워드는 삭제하지 않고 selected를 false로 변경
      if (item && item.isExisting) {
        return prev.map((keyword, i) =>
          i === index ? { ...keyword, selected: false } : keyword
        );
      }

      // 새로 생성된 키워드는 삭제
      return prev.filter((item, i) => i !== index);
    });
  };

  // 키워드 업데이트 함수
  const updateKeywordRank = (index, field, value) => {
    setKeywordRanks((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  // 모든 키워드(기존 + 새로 생성된)의 순위 조회 API 호출 함수
  const fetchKeywordRanksForAllKeywords = async (allKeywords) => {
    if (!allKeywords || allKeywords.length === 0 || !adData.productId) {
      setIsLoading(false);
      return;
    }

    try {
      // 모든 키워드 데이터 준비
      const subKeywords = allKeywords
        .filter((item) => item.keyword && item.keyword.trim() !== "")
        .map((item) => ({
          productId: adData.productId,
          name: item.keyword,
          pageSize: 1,
        }));

      const rankData = {
        sub: subKeywords,
      };

      const rankResponse = await getNaverShoppingRankAPI(rankData);

      // 응답 데이터로 키워드 순위 업데이트
      if (rankResponse.data?.sub && Array.isArray(rankResponse.data.sub)) {
        setKeywordRanks((prevKeywordRanks) => {
          // 먼저 모든 키워드의 순위를 업데이트
          const updatedKeywordRanks = prevKeywordRanks.map((item) => {
            // sub 응답에서 해당 키워드 찾기
            const subResponse = rankResponse.data.sub.find(
              (subItem) => subItem.name === item.keyword
            );

            if (subResponse) {
              const newRank = subResponse.rank || 999;

              return {
                ...item,
                rank: newRank,
              };
            }

            return item;
          });

          // 정렬: 기존 키워드를 맨 위에, 그 다음에 낮은 순위 순으로 정렬
          const sortedKeywordRanks = updatedKeywordRanks.sort((a, b) => {
            // 1. 기존 키워드가 우선 (isExisting이 true인 것들이 먼저)
            if (a.isExisting && !b.isExisting) return -1;
            if (!a.isExisting && b.isExisting) return 1;

            // 2. 기존 키워드들 사이에서는 낮은 순위가 먼저
            if (a.isExisting && b.isExisting) {
              // 999는 가장 마지막으로
              if (a.rank === 999 && b.rank !== 999) return 1;
              if (a.rank !== 999 && b.rank === 999) return -1;
              if (a.rank === 999 && b.rank === 999) return 0;

              // 0은 가장 마지막으로 (999 다음)
              if (a.rank === 0 && b.rank !== 0) return 1;
              if (a.rank !== 0 && b.rank === 0) return -1;
              if (a.rank === 0 && b.rank === 0) return 0;

              // 일반적인 순위는 낮은 숫자가 먼저
              return a.rank - b.rank;
            }

            // 3. 새로 생성된 키워드들 사이에서도 낮은 순위가 먼저
            // 999는 가장 마지막으로
            if (a.rank === 999 && b.rank !== 999) return 1;
            if (a.rank !== 999 && b.rank === 999) return -1;
            if (a.rank === 999 && b.rank === 999) return 0;

            // 0은 가장 마지막으로 (999 다음)
            if (a.rank === 0 && b.rank !== 0) return 1;
            if (a.rank !== 0 && b.rank === 0) return -1;
            if (a.rank === 0 && b.rank === 0) return 0;

            // 일반적인 순위는 낮은 숫자가 먼저
            return a.rank - b.rank;
          });

          return sortedKeywordRanks;
        });
      }
    } catch (error) {
      console.error("키워드 순위 조회 실패:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 개별 키워드 순위 조회 API 호출 함수
  const fetchKeywordRank = async (keyword, index) => {
    if (!keyword || !keyword.trim() || !adData.productId) {
      return;
    }

    // 로딩 상태 시작
    setKeywordLoadingIndex(index);

    try {
      // 해당 키워드 1개만 순위 조회
      const subKeywords = [
        {
          productId: adData.productId,
          name: keyword,
          pageSize: 1,
        },
      ];

      const rankData = {
        sub: subKeywords,
      };

      const rankResponse = await getNaverShoppingRankAPI(rankData);

      if (rankResponse.data?.sub && Array.isArray(rankResponse.data.sub)) {
        const subResponse = rankResponse.data.sub[0];
        if (subResponse) {
          const newRank = subResponse.rank || 999;

          // 순위만 업데이트 (정렬 없음)
          setKeywordRanks((prevKeywordRanks) => {
            return prevKeywordRanks.map((item, i) => {
              if (i === index) {
                return {
                  ...item,
                  rank: newRank,
                };
              }
              return item;
            });
          });
        }
      }
    } catch (error) {
      console.error("키워드 순위 조회 실패:", error);
    } finally {
      // 로딩 상태 종료
      setKeywordLoadingIndex(null);
    }
  };

  // 새로운 키워드의 순위 조회 API 호출 함수
  const fetchKeywordRankForNewKeyword = async (keyword, index) => {
    if (!keyword || !keyword.trim() || !adData.productId) {
      return;
    }

    // 로딩 상태 시작
    setKeywordLoadingIndex(index);

    try {
      // 해당 키워드 1개만 순위 조회
      const subKeywords = [
        {
          productId: adData.productId,
          name: keyword,
          pageSize: 1,
        },
      ];

      const rankData = {
        sub: subKeywords,
      };

      const rankResponse = await getNaverShoppingRankAPI(rankData);

      if (rankResponse.data?.sub && Array.isArray(rankResponse.data.sub)) {
        const subResponse = rankResponse.data.sub[0];
        if (subResponse) {
          const newRank = subResponse.rank || 999;

          // 순위만 업데이트 (정렬 없음)
          setKeywordRanks((prevKeywordRanks) => {
            const updatedKeywordRanks = prevKeywordRanks.map((item, i) => {
              if (i === index) {
                return {
                  ...item,
                  rank: newRank,
                };
              }
              return item;
            });

            return updatedKeywordRanks;
          });
        }
      }
    } catch (error) {
      console.error("키워드 순위 조회 실패:", error);
    } finally {
      // 로딩 상태 종료
      setKeywordLoadingIndex(null);
    }
  };

  // 키워드 입력 필드 포커스 아웃 처리
  const handleKeywordBlur = (keyword, index, isTemp = false, tempId = null) => {
    const trimmedKeyword = keyword.trim();

    if (isTemp && trimmedKeyword !== "") {
      // 임시 키워드 처리
      handleNewKeywordInput(trimmedKeyword, tempId);
    } else if (!keywordRanks[index]?.isExisting && trimmedKeyword !== "") {
      // 기존 키워드 처리 (기존 로직 유지)
      handleNewKeywordInput(trimmedKeyword, index);
    }
  };

  // 키워드 입력 필드 키 다운 처리 (엔터 키)
  const handleKeywordKeyDown = (
    e,
    keyword,
    index,
    isTemp = false,
    tempId = null
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedKeyword = keyword.trim();

      if (isTemp && trimmedKeyword !== "") {
        // 임시 키워드 처리
        handleNewKeywordInput(trimmedKeyword, tempId);
      } else if (!keywordRanks[index]?.isExisting && trimmedKeyword !== "") {
        // 기존 키워드 처리 (기존 로직 유지)
        handleNewKeywordInput(trimmedKeyword, index);
      }
      e.target.blur();
    }
  };

  // 새로운 키워드 입력 처리 함수 (임시 키워드 및 기존 키워드 처리)
  const handleNewKeywordInput = (keyword, identifier) => {
    const trimmedKeyword = keyword.trim();

    // keywordRanks에 동일한 키워드가 있는지 확인 (기존 키워드든 새로 생성된 키워드든)
    const existingKeyword = keywordRanks.find((item) => {
      const keywordMatch =
        item.keyword.toLowerCase().trim() === trimmedKeyword.toLowerCase();
      return keywordMatch; // isExisting 조건 제거 - 모든 키워드와 비교
    });

    // keywordData에서도 SUB 타입 키워드들과 중복 체크
    const keywordDataMatch = (keywordData || [])
      .filter((k) => k.type === "SUB")
      .find(
        (k) =>
          (k.name || "").toLowerCase().trim() === trimmedKeyword.toLowerCase()
      );

    // identifier가 임시 키워드 ID인지 확인
    const isTempKeyword =
      typeof identifier === "string" && identifier.startsWith("temp_");

    if (existingKeyword || keywordDataMatch) {
      // 동일한 키워드가 있으면 해당 키워드를 selected: true로 설정하고 입력 필드 삭제
      if (existingKeyword) {
        setKeywordRanks((prevKeywordRanks) => {
          const updatedRanks = prevKeywordRanks.map((item) => {
            if (item.keyword === existingKeyword.keyword) {
              return {
                ...item,
                selected: true,
              };
            }
            return item;
          });

          return updatedRanks;
        });
      }

      // 동일한 키워드를 찾았으므로 모든 임시 키워드를 비우기
      setTempKeywords([]);

      if (!isTempKeyword) {
        // 기존 키워드에서 해당 인덱스의 새로운 키워드 삭제
        setKeywordRanks((prevKeywordRanks) => {
          const filteredKeywordRanks = prevKeywordRanks.filter((item, i) => {
            const shouldRemove =
              i === identifier &&
              !item.isExisting &&
              item.keyword === trimmedKeyword;
            return !shouldRemove;
          });
          return filteredKeywordRanks;
        });
      }
    } else {
      if (isTempKeyword) {
        // 임시 키워드를 실제 키워드로 이동하고 순위 조회
        setTempKeywords((prevTempKeywords) => {
          const targetTempKeyword = prevTempKeywords.find(
            (item) => item.tempId === identifier
          );

          if (targetTempKeyword) {
            // 실제 키워드에 추가
            setKeywordRanks((prevKeywordRanks) => {
              const newKeyword = {
                ...targetTempKeyword,
                keyword: trimmedKeyword,
              };
              const updatedKeywordRanks = [...prevKeywordRanks, newKeyword];

              // 순위 조회 실행
              fetchKeywordRankForNewKeyword(
                trimmedKeyword,
                updatedKeywordRanks.length - 1
              );

              return updatedKeywordRanks;
            });

            // 모든 임시 키워드 삭제 (순위 조회 후 정리)
            return [];
          }

          return prevTempKeywords;
        });
      } else {
        // 기존 키워드의 경우 순위 조회만 실행
        fetchKeywordRank(trimmedKeyword, identifier);
      }
    }
  };

  // 수정 버튼 클릭 처리
  const handleSave = async () => {
    // 선택된 키워드들 중 keyword가 빈 문자열이 아닌 것들만 필터링
    const selectedKeywords = keywordRanks.filter(
      (item) => item.selected && item.keyword && item.keyword.trim() !== ""
    );
    const allowRanking = zoomSettings?.allowRanking || 30;
    // 특정 유저는 순위 제한 예외
    const effectiveAllowRanking = userIsSpecial ? Infinity : allowRanking;

    // 최소 1개, 최대 10개 검증
    if (selectedKeywords.length === 0) {
      alert("최소 1개의 키워드를 선택해주세요.");
      return;
    }

    if (selectedKeywords.length > 10) {
      alert("최대 10개까지만 선택할 수 있습니다.");
      return;
    }

    const invalidKeywords = selectedKeywords.filter(
      (item) => item.rank > effectiveAllowRanking
    );

    if (invalidKeywords.length > 0) {
      alert(`${allowRanking}위내의 키워드만 ON해주세요.`);
      return;
    }

    // 원래 기존 키워드들과 비교
    const originalKeywords = keywordData || [];

    // 기존 키워드와 새로운 키워드 구분
    const existingKeywords = selectedKeywords.filter((item) => item.isExisting);
    const newKeywords = selectedKeywords.filter((item) => !item.isExisting);

    // 기존 키워드들과 비교
    if (newKeywords.length > 0) {
      // 선택되지 않은 기존 키워드들 찾기
      const unselectedExistingKeywords = keywordRanks.filter(
        (item) => !item.selected && item.isExisting
      );

      // 새로 추가된 키워드들을 처리
      const keywordsToProcess = [...newKeywords];

      // 1. 선택되지 않은 기존 키워드들에 새 키워드들을 업데이트
      for (
        let i = 0;
        i <
        Math.min(unselectedExistingKeywords.length, keywordsToProcess.length);
        i++
      ) {
        const existingKeyword = unselectedExistingKeywords[i];
        const newKeyword = keywordsToProcess[i];

        try {
          await updateKeywordAPI(
            existingKeyword.keyword_id,
            newKeyword.keyword
          );
        } catch (error) {
          console.error("키워드 업데이트 실패:", error);
        }
      }

      // 2. 남은 새 키워드들을 extension에 추가
      const remainingNewKeywords = keywordsToProcess.slice(
        unselectedExistingKeywords.length
      );

      if (remainingNewKeywords.length > 0) {
        const extensionData = {
          ad_id: adData.ad_id || adData.id,
          subKeywords: remainingNewKeywords.map((keyword) => ({
            name: keyword.keyword,
          })),
        };

        try {
          await patchAdExtensionAPI(extensionData);
        } catch (error) {
          console.error("Extension 키워드 추가 실패:", error);
        }
      }

      // 3. 남은 기존 키워드들을 삭제
      const remainingExistingKeywords = unselectedExistingKeywords.slice(
        keywordsToProcess.length
      );

      if (remainingExistingKeywords.length > 0) {
        for (const existingKeyword of remainingExistingKeywords) {
          try {
            await deleteKeywordAPI(
              parseInt(existingKeyword.keyword_id) ||
                Number(existingKeyword.keyword_id)
            );
          } catch (error) {
            console.error("키워드 삭제 실패:", error);
          }
        }
      }
    } else {
      // 선택되지 않은 기존 키워드들이 있으면 삭제
      const unselectedExistingKeywords = keywordRanks.filter(
        (item) => !item.selected && item.isExisting
      );

      if (unselectedExistingKeywords.length > 0) {
        for (const existingKeyword of unselectedExistingKeywords) {
          try {
            await deleteKeywordAPI(
              parseInt(existingKeyword.keyword_id) ||
                Number(existingKeyword.keyword_id)
            );
          } catch (error) {
            console.error("키워드 삭제 실패:", error);
          }
        }
      }
    }

    // onSave 콜백 호출
    onSave(selectedKeywords);

    // 팝업 닫기
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="ad-detail-modal-overlay" style={{ zIndex: 2000 }}>
      <div className="ad-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="modal-header">
          <h2>키워드 수정</h2>
        </div>

        {/* 내용 */}
        <div className="modal-content">
          {isLoading ? (
            <div className="my-ad-loading-container">
              <div className="my-ad-loading-spinner"></div>
              <p>키워드 순위를 조회하고 있습니다...</p>
            </div>
          ) : (
            <div className="my-ad-keyword-edit-section">
              <div className="my-ad-keyword-edit-layout">
                {/* 왼쪽: 키워드 선택 영역 */}
                <div className="my-ad-keyword-selection-area">
                  <div className="my-ad-selection-header">
                    <h4>키워드 선택</h4>
                    <div className="my-ad-instruction-text">
                      <p>*최소 키워드 1개는 on해야 주문하기가 가능합니다</p>
                      <p>
                        *키워드를 눌러 CPC광고가 나오는 키워드는 해제해주세요.
                      </p>
                      <p className="my-ad-important">
                        *{zoomSettings?.allowRanking || 30}위내의 키워드만
                        ON해주세요.
                      </p>
                    </div>
                    <div className="my-ad-selection-info">
                      <span>
                        선택된 키워드:{" "}
                        {keywordRanks.filter((item) => item.selected).length}/10
                      </span>
                    </div>
                  </div>

                  <div className="my-ad-keyword-list">
                    <div className="my-ad-keyword-list-header">
                      <div className="my-ad-header-keyword">키워드</div>
                      <div className="my-ad-header-rank">순위</div>
                      <div className="my-ad-header-status">상태</div>
                    </div>

                    {keywordRanks.map((item, index) => {
                      // 특정 유저는 순위 제한 예외
                      const effectiveAllowRanking = userIsSpecial
                        ? Infinity
                        : zoomSettings?.allowRanking || 30;

                      return (
                      <div key={index} className="my-ad-keyword-list-item">
                        <div className="my-ad-item-keyword">
                          {item.keyword}
                          {item.isExisting && (
                            <span className="my-ad-existing-badge">기존</span>
                          )}
                        </div>
                        <div
                          className={`my-ad-item-rank ${
                            item.rank > effectiveAllowRanking
                              ? "error"
                              : item.rank === 999
                              ? "empty"
                              : "success"
                          }`}
                        >
                          {item.rank === 999
                            ? "999+위"
                            : item.rank === 0
                            ? "-위"
                            : `${item.rank}위`}
                        </div>
                        <div className="my-ad-item-status">
                          <label
                            className={`my-ad-switch ${
                              item.rank > effectiveAllowRanking &&
                              !item.selected
                                ? "disabled"
                                : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={item.selected}
                              onChange={() => handleToggle(index)}
                              disabled={
                                item.rank > effectiveAllowRanking &&
                                !item.selected
                              }
                            />
                            <span className="my-ad-slider round"></span>
                          </label>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>

                {/* 오른쪽: 선택된 키워드 영역 */}
                <div className="my-ad-selected-keywords-area">
                  <div className="my-ad-selected-header">
                    <h4>선택된 키워드</h4>
                  </div>

                  <div className="my-ad-selected-keywords-list">
                    {keywordRanks
                      .filter((item) => item.selected)
                      .map((item, index) => {
                        const selectedKeywords = keywordRanks.filter(
                          (item) => item.selected
                        );
                        const actualIndex = keywordRanks.findIndex(
                          (keywordItem) =>
                            keywordItem.selected &&
                            selectedKeywords[index] === keywordItem
                        );
                        // 특정 유저는 순위 제한 예외
                        const effectiveAllowRanking = userIsSpecial
                          ? Infinity
                          : zoomSettings?.allowRanking || 30;

                        return (
                          <div
                            key={actualIndex}
                            className={`my-ad-selected-keyword-box ${
                              item.rank > effectiveAllowRanking
                                ? "error"
                                : item.rank === 999
                                ? "empty"
                                : "existing"
                            }`}
                          >
                            <div className="my-ad-selected-keyword-content">
                              <div className="my-ad-selected-keyword-header">
                                <div
                                  className={`my-ad-selected-keyword-rank ${
                                    item.rank > effectiveAllowRanking
                                      ? "error"
                                      : item.rank === 999
                                      ? "empty"
                                      : "success"
                                  }`}
                                >
                                  {item.rank === 999
                                    ? "999+위"
                                    : item.rank === 0
                                    ? "-위"
                                    : `${item.rank}위`}
                                </div>
                                <div className="my-ad-selected-keyword-badges">
                                  {item.isExisting && (
                                    <span className="my-ad-selected-existing-badge">
                                      기존
                                    </span>
                                  )}
                                  <button
                                    className={`my-ad-selected-remove-keyword ${
                                      item.rank > effectiveAllowRanking
                                        ? "error"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      removeKeywordRank(actualIndex)
                                    }
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                              <span className="my-ad-selected-keyword-label">
                                {index === 0 ? "*" : ""}
                                유입용 키워드{index + 1}
                                {index === 0 ? "(필수)" : "(선택)"}
                              </span>
                              <input
                                type="text"
                                value={item.keyword}
                                onChange={(e) =>
                                  updateKeywordRank(
                                    actualIndex,
                                    "keyword",
                                    e.target.value
                                  )
                                }
                                onBlur={() =>
                                  handleKeywordBlur(item.keyword, index)
                                }
                                onKeyDown={(e) =>
                                  handleKeywordKeyDown(e, item.keyword, index)
                                }
                                className={`my-ad-selected-keyword-input-field ${
                                  keywordLoadingIndex === index ? "loading" : ""
                                }`}
                                disabled={
                                  item.isExisting ||
                                  keywordLoadingIndex === index
                                }
                                readOnly={item.isExisting || item.rank !== 0}
                              />
                              {keywordLoadingIndex === index && (
                                <div className="my-ad-selected-keyword-loading">
                                  순위 조회 중...
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                    {/* 임시 키워드 렌더링 */}
                    {tempKeywords.map((tempItem, tempIndex) => (
                      <div
                        key={tempItem.tempId}
                        className="my-ad-selected-keyword-box new"
                      >
                        <div className="my-ad-selected-keyword-content">
                          <div className="my-ad-selected-keyword-header">
                            <div className="my-ad-selected-keyword-rank empty">
                              -위
                            </div>
                            <div className="my-ad-selected-keyword-badges">
                              <button
                                className="my-ad-selected-remove-keyword"
                                onClick={() => {
                                  setTempKeywords((prev) =>
                                    prev.filter(
                                      (item) => item.tempId !== tempItem.tempId
                                    )
                                  );
                                }}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          <span className="my-ad-selected-keyword-label">
                            유입용 키워드
                            {keywordRanks.filter((item) => item.selected)
                              .length +
                              tempIndex +
                              1}
                            (선택)
                          </span>
                          <input
                            type="text"
                            value={tempItem.keyword}
                            onChange={(e) => {
                              setTempKeywords((prev) =>
                                prev.map((item) =>
                                  item.tempId === tempItem.tempId
                                    ? { ...item, keyword: e.target.value }
                                    : item
                                )
                              );
                            }}
                            onBlur={() =>
                              handleKeywordBlur(
                                tempItem.keyword,
                                tempIndex,
                                true,
                                tempItem.tempId
                              )
                            }
                            onKeyDown={(e) =>
                              handleKeywordKeyDown(
                                e,
                                tempItem.keyword,
                                tempIndex,
                                true,
                                tempItem.tempId
                              )
                            }
                            className="my-ad-selected-keyword-input-field"
                            placeholder="키워드를 입력하세요"
                          />
                        </div>
                      </div>
                    ))}

                    {keywordRanks.filter((item) => item.selected).length +
                      tempKeywords.length <
                      10 && (
                      <button
                        className="my-ad-selected-add-keyword-btn"
                        onClick={addKeywordRank}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="modal-footer">
          <button className="ad-detail-edit-keyword-btn" onClick={handleSave}>
            수정
          </button>
          <button className="ad-detail-close-btn" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShoppingKeywordEditModal;
